import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { documentCheckCreateSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  Form,
  FormRootError,
  InputField,
  SelectField,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@blimo/ui';
import { usePropertyOptions } from '../../components/property-options.js';

/**
 * Anlegen einer Belegprüfung (`document-checks.manage`); die sieben Ordner legt die API an.
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void, defaultPropertyId?: string, onCreated?: (check: any) => void }} props
 */
export function DocumentCheckCreateSheet({ open, onOpenChange, defaultPropertyId, onCreated }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Belegprüfung anlegen</SheetTitle>
          <SheetDescription>Die sieben Standardordner werden automatisch angelegt.</SheetDescription>
        </SheetHeader>
        {open && <CreateForm defaultPropertyId={defaultPropertyId} onDone={() => onOpenChange(false)} onCreated={onCreated} />}
      </SheetContent>
    </Sheet>
  );
}

function CreateForm({ defaultPropertyId, onDone, onCreated }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { options } = usePropertyOptions();
  const form = useForm({
    resolver: zodResolver(documentCheckCreateSchema),
    defaultValues: { property_id: defaultPropertyId ?? '', fiscal_year: String(new Date().getFullYear() - 1), due_on: '' },
  });
  const mutation = useMutation({
    mutationFn: (payload) => api.admin.documentChecks.create(payload),
    onSuccess: (check) => {
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('document-checks') });
      toast.success('Belegprüfung angelegt');
      onDone();
      onCreated?.(check);
    },
  });
  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({ property_id: Number(values.property_id), fiscal_year: Number(values.fiscal_year), due_on: values.due_on || null });
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['property_id', 'fiscal_year', 'due_on'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4 px-4">
        <FormRootError />
        <SelectField name="property_id" label="Liegenschaft" options={options} placeholder="Bitte wählen …" />
        <InputField name="fiscal_year" label="Abrechnungsjahr" inputMode="numeric" maxLength={4} />
        <InputField name="due_on" label="Abschluss bis" type="date" description="Optional – Termin für die Kassenprüfer." />
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            Anlegen
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
